import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, X } from 'lucide-react';
import type { Tenant } from '@/api/types';
import type { TenantPhoneLookupResult } from './useTenantPhoneLookup';

/** Renders the ACTIVE_WITH_BED (offer bed swap), CHECKED_OUT (offer to fetch prior details), or
 *  NO_PROFILE (offer to prefill known name/email) prompts surfaced by useTenantPhoneLookup. Every
 *  outcome is a user choice — nothing is ever applied to the form silently. NONE never renders. */
export function TenantPhoneMatchDialog({ result, role, currentHostelId, onFetchDetails, onPrefillBasic, onStartFresh, onClose }: {
  result: TenantPhoneLookupResult;
  role: 'owner' | 'supervisor';
  /** The hostel the Owner/Supervisor is currently admitting into — swapping beds only works
   *  within a single hostel, so the swap CTA is only offered when the matched active tenant
   *  is in this same hostel; otherwise this is just informational (they'd need to be checked
   *  out of their current hostel first, which admission would already block server-side). */
  currentHostelId: string;
  onFetchDetails: (tenant: Tenant) => void;
  onPrefillBasic: (fullName: string, email: string) => void;
  onStartFresh: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenant = result.tenant;
  if (result.matchType === 'NONE') return null;
  if (result.matchType !== 'NO_PROFILE' && !tenant) return null;

  const sameHostel = result.matchType === 'ACTIVE_WITH_BED' && tenant?.hostelId === currentHostelId;

  const title =
    result.matchType === 'ACTIVE_WITH_BED' ? t('tenant.existingTenantFound', 'Existing Tenant Found') :
    result.matchType === 'CHECKED_OUT' ? t('tenant.previousTenantFound', 'Previous Tenant Found') :
    t('tenant.existingAccountFound', 'Existing Account Found');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-chrome-topbar-border">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            <span className="font-semibold text-sm">{title}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          {result.matchType === 'ACTIVE_WITH_BED' && tenant && sameHostel && (
            <p className="text-sm text-gray-700">
              {t('tenant.phoneAlreadyActive',
                'This phone belongs to {{name}}, already active in {{room}}-{{bed}} at {{hostel}}. Do you want to swap beds instead?',
                { name: tenant.fullName, room: tenant.roomNumber ?? '—', bed: tenant.bedLabel ?? '—', hostel: result.hostelName ?? '' })}
            </p>
          )}
          {result.matchType === 'ACTIVE_WITH_BED' && tenant && !sameHostel && (
            <p className="text-sm text-gray-700">
              {t('tenant.phoneAlreadyActiveElsewhere',
                'This phone belongs to {{name}}, already active in {{room}}-{{bed}} at {{hostel}}. They need to be checked out there before they can be admitted here.',
                { name: tenant.fullName, room: tenant.roomNumber ?? '—', bed: tenant.bedLabel ?? '—', hostel: result.hostelName ?? '' })}
            </p>
          )}
          {result.matchType === 'CHECKED_OUT' && tenant && (
            <p className="text-sm text-gray-700">
              {t('tenant.foundPreviousTenant',
                'Found a previous tenant record for {{name}}, checked out {{date}} from {{room}}-{{bed}} at {{hostel}}. Fetch their details to speed up re-admission?',
                { name: tenant.fullName, date: tenant.checkoutDate ?? '—', room: tenant.roomNumber ?? '—', bed: tenant.bedLabel ?? '—', hostel: result.hostelName ?? '' })}
            </p>
          )}
          {result.matchType === 'NO_PROFILE' && (
            <p className="text-sm text-gray-700">
              {t('tenant.foundExistingAccount',
                'Found an existing account for {{name}} with this phone number, but no admission on file yet. Use their name and email to prefill the form?',
                { name: result.matchedFullName ?? t('tenant.thisUser', 'this user') })}
            </p>
          )}

          <div className="flex gap-2">
            {result.matchType === 'ACTIVE_WITH_BED' && tenant && sameHostel && (
              <>
                <button
                  onClick={() => {
                    const search = { tenantId: tenant.id, hostelId: tenant.hostelId };
                    if (role === 'owner') void navigate({ to: '/owner/swap-beds', search });
                    else void navigate({ to: '/supervisor/swap-beds', search });
                  }}
                  className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg py-2 text-sm font-medium hover:opacity-90 transition"
                >
                  {t('tenant.goToSwapBeds', 'Go to Swap Beds')}
                </button>
                <button onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm">
                  {t('common.cancel')}
                </button>
              </>
            )}
            {result.matchType === 'ACTIVE_WITH_BED' && tenant && !sameHostel && (
              <button onClick={onClose} className="flex-1 border rounded-lg py-2 text-sm">
                {t('common.close', 'Close')}
              </button>
            )}
            {result.matchType === 'CHECKED_OUT' && tenant && (
              <>
                <button
                  onClick={() => onFetchDetails(tenant)}
                  className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg py-2 text-sm font-medium hover:opacity-90 transition"
                >
                  {t('tenant.yesFetchDetails', 'Yes, fetch details')}
                </button>
                <button onClick={onStartFresh} className="flex-1 border rounded-lg py-2 text-sm">
                  {t('tenant.noStartFresh', 'No, start fresh')}
                </button>
              </>
            )}
            {result.matchType === 'NO_PROFILE' && (
              <>
                <button
                  onClick={() => onPrefillBasic(result.matchedFullName ?? '', result.matchedEmail ?? '')}
                  className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-lg py-2 text-sm font-medium hover:opacity-90 transition"
                >
                  {t('tenant.yesPrefillDetails', 'Yes, prefill details')}
                </button>
                <button onClick={onStartFresh} className="flex-1 border rounded-lg py-2 text-sm">
                  {t('tenant.noStartFresh', 'No, start fresh')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
